import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { toast, formatNumInput, parseNumInput } from '../../lib/utils';
import { RETAIL_STORES } from '../../lib/constants';
import SalesTabNav from './SalesTabNav';

// mode: 'full'(조회+입력 탭) | 'view'(현황 조회만) | 'input'(매출 입력만)
export default function BizSalesPage({ profile, setPage, mode = 'full' }) {
  const today = new Date().toISOString().slice(0,10);
  const [tab, setTab] = useState(mode === 'input' ? 'input' : 'list'); // 'list' | 'input'

  // 입력 폼 (거래 단위)
  const [soldAt,         setSoldAt]         = useState(today);
  const [companyId,      setCompanyId]      = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState(''); // '용차' | '택배'
  const [memo,           setMemo]           = useState('');
  const [saving,         setSaving]         = useState(false);

  // 배송지 정보 (거래 단위)
  const [recipName,       setRecipName]       = useState('');
  const [recipPhone,      setRecipPhone]      = useState('');
  const [recipAddr,       setRecipAddr]       = useState('');
  const [recipAddrDetail, setRecipAddrDetail] = useState('');

  // 상품 라인
  const newLine = () => ({
    id: Date.now()+Math.random(),
    productId:'', productSearch:'', showSuggestions:false,
    brandId:'', quantity:1, supplyPrice:'', isGift:false,
  });
  const [lines, setLines] = useState([newLine()]);

  // 매출조회 행 인라인 편집 / 삭제 (본사·어드민만)
  const canManage = !!profile && (profile.role === 'admin' || profile.job_title === '담당자');
  const [editingId,  setEditingId]  = useState(null);
  const [editData,   setEditData]   = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // 반품 처리 모달
  const [returnTarget, setReturnTarget] = useState(null); // 반품 대상 매출 행
  const [returnDate,   setReturnDate]   = useState(today);
  const [returnQty,    setReturnQty]    = useState(1);
  const [returnMemo,   setReturnMemo]   = useState('');
  const [savingReturn, setSavingReturn] = useState(false);

  // 신규업체 추가 모달
  const [showAddCompany,   setShowAddCompany]   = useState(false);
  const [newCompName,      setNewCompName]      = useState('');
  const [newCompContact,   setNewCompContact]   = useState('');
  const [newCompPhone,     setNewCompPhone]     = useState('');
  const [newCompAddr,      setNewCompAddr]      = useState('');
  const [newCompAddrDetail,setNewCompAddrDetail]= useState('');
  const [savingCompany,    setSavingCompany]    = useState(false);

  // 데이터
  const [companies, setCompanies] = useState([]);
  const [brands,    setBrands]    = useState([]);
  const [allProds,  setAllProds]  = useState([]);
  const [sales,     setSales]     = useState([]);
  const [loading,   setLoading]   = useState(false);

  // 조회 필터
  const now = new Date();
  const pad = n => String(n).padStart(2,'0');
  const monthStart = `${now.getFullYear()}-${pad(now.getMonth()+1)}-01`;
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
  const [fFrom,   setFFrom]   = useState(monthStart);
  const [fTo,     setFTo]     = useState(todayStr);
  const [fCompany,setFCompany]= useState('');
  const [specialNames, setSpecialNames] = useState([]); // 특판(매장 판매입력) 지점명 목록

  useEffect(() => {
    supabase.from('biz_companies').select('*').order('name').then(({data}) => setCompanies(data||[]));
    supabase.from('brands').select('*').order('name').then(({data}) => setBrands(data||[]));
    supabase.from('products').select('*').order('name').then(({data}) => setAllProds(data||[]));
  }, []);

  const fetchSales = useCallback(async () => {
    setLoading(true);
    const from = fFrom;
    const to = fTo;
    const isSp = String(fCompany).startsWith('sp:');

    // 1) biz_sales (B2B 거래처)
    let bizData = [];
    if (!isSp) {
      let q = supabase.from('biz_sales')
        .select('*, company:biz_companies(name), brand:brands(name), product:products(name, code, cost)')
        .gte('sold_at', from).lte('sold_at', to)
        .order('sold_at', {ascending:false});
      if (fCompany) q = q.eq('company_id', fCompany);
      const {data} = await q;
      bizData = data || [];
    }

    // 2) 특판 sales = sales 테이블에서 정식 백화점(RETAIL_STORES)이 아닌 store_name — 페이징
    //    (표기 편차와 무관하게 잡히도록 화이트리스트가 아닌 '정식 매장 제외' 방식)
    const notInList = '(' + RETAIL_STORES.map(s => `"${s}"`).join(',') + ')';
    let spAll = [];
    let start = 0; const PAGE = 1000;
    while (true) {
      const { data, error } = await supabase.from('sales')
        .select('id, sold_at, store_name, branch_name, price, quantity, payment, brand:brands(name), product:products(name, code, cost)')
        .neq('payment', '구매이력')
        .not('store_name', 'in', notInList)
        .not('store_name', 'is', null)
        .gte('sold_at', from).lte('sold_at', to)
        .order('id').range(start, start + PAGE - 1);
      if (error) break;
      if (!data || data.length === 0) break;
      spAll.push(...data);
      if (data.length < PAGE) break;
      start += PAGE;
    }
    spAll = spAll.filter(r => r.payment !== '강좌매출'); // 강좌매출 제외
    const names = [...new Set(spAll.map(r => r.branch_name || r.store_name).filter(Boolean))].sort();
    setSpecialNames(names);

    let spRows = spAll.map(r => ({
      id: 'sp-' + r.id,
      _special: true,
      sold_at: r.sold_at,
      company_name: r.branch_name || r.store_name,
      brand_name: r.brand?.name || null,
      product_name: r.product?.name || null,
      product_code: r.product?.code || '',
      unit_cost: Number(r.product?.cost) || 0,
      quantity: r.quantity,
      supply_price: r.price, // 단가 (반품은 음수)
      delivery_method: null,
      memo: null,
    }));
    if (isSp) {
      spRows = spRows.filter(r => r.company_name === fCompany.slice(3));
    } else if (fCompany) {
      // 특정 B2B 업체 선택 — 같은 이름의 특판(매장 판매입력) 매출도 함께 포함
      // (국군복지단·중소기업명품마루대구점처럼 거래처 등록돼 있으나 판매입력으로 들어온 건)
      const compName = (companies.find(c => String(c.id) === String(fCompany)) || {}).name || '';
      spRows = spRows.filter(r => r.company_name === compName);
    }

    const merged = [...bizData, ...spRows].sort((a, b) => String(b.sold_at).localeCompare(String(a.sold_at)));
    setSales(merged);
    setLoading(false);
  }, [fFrom, fTo, fCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (tab==='list') fetchSales(); }, [fetchSales, tab]);

  // 라인 헬퍼
  const updateLine = (id, field, value) => {
    setLines(prev => prev.map(l => {
      if (l.id !== id) return l;
      const updated = { ...l, [field]: value };
      if (field === 'productId') {
        const prod = allProds.find(p => String(p.id) === String(value));
        if (prod) {
          updated.brandId = String(prod.brand_id || '');
          if (!updated.supplyPrice) updated.supplyPrice = String(prod.price || '');
        }
        updated.showSuggestions = false;
      }
      return updated;
    }));
  };
  const addLine = () => setLines(prev => [...prev, newLine()]);
  const removeLine = (id) => setLines(prev => {
    if (prev.length <= 1) return [newLine()];
    return prev.filter(l => l.id !== id);
  });
  const selectProdForLine = (lineId, p) => {
    setLines(prev => prev.map(l => {
      if (l.id !== lineId) return l;
      return {
        ...l,
        productId: String(p.id),
        productSearch: p.name,
        brandId: String(p.brand_id || ''),
        supplyPrice: l.supplyPrice || String(p.price || ''),
        showSuggestions: false,
      };
    }));
  };

  const openAddCompanyModal = () => {
    setNewCompName(''); setNewCompContact(''); setNewCompPhone('');
    setNewCompAddr(''); setNewCompAddrDetail('');
    setShowAddCompany(true);
  };

  // 다음(카카오) 우편번호 — 신규업체 주소 검색
  const openCompanyAddrSearch = () => {
    const launch = () => {
      // eslint-disable-next-line no-new, new-cap
      new window.daum.Postcode({
        oncomplete: (data) => {
          const addr = data.roadAddress || data.jibunAddress || '';
          const zip = data.zonecode ? `(${data.zonecode}) ` : '';
          setNewCompAddr(`${zip}${addr}`);
          setTimeout(() => {
            const el = document.getElementById('new-comp-addr-detail');
            if (el) el.focus();
          }, 0);
        },
      }).open();
    };
    if (window.daum?.Postcode) {
      launch();
    } else {
      const script = document.createElement('script');
      script.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      script.onload  = launch;
      script.onerror = () => toast('주소 검색 서비스 로드 실패', 'err');
      document.body.appendChild(script);
    }
  };

  // 다음(카카오) 우편번호 — 배송지 주소 검색
  const openShippingAddrSearch = () => {
    const launch = () => {
      // eslint-disable-next-line no-new, new-cap
      new window.daum.Postcode({
        oncomplete: (data) => {
          const addr = data.roadAddress || data.jibunAddress || '';
          const zip = data.zonecode ? `(${data.zonecode}) ` : '';
          setRecipAddr(`${zip}${addr}`);
          setTimeout(() => {
            const el = document.getElementById('biz-recip-addr-detail');
            if (el) el.focus();
          }, 0);
        },
      }).open();
    };
    if (window.daum?.Postcode) {
      launch();
    } else {
      const script = document.createElement('script');
      script.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      script.onload  = launch;
      script.onerror = () => toast('주소 검색 서비스 로드 실패', 'err');
      document.body.appendChild(script);
    }
  };

  // 업체 선택 시 그 업체의 등록 정보(담당자·연락처·주소)로 배송지 자동 채움.
  // 업체를 바꾸면 항상 새 업체 정보로 덮어쓴다 (사용자가 수정한 내용도 새 업체 기준으로 갱신).
  useEffect(() => {
    if (!companyId) {
      setRecipName(''); setRecipPhone(''); setRecipAddr(''); setRecipAddrDetail('');
      return;
    }
    const c = companies.find(x => String(x.id) === String(companyId));
    if (!c) return;
    setRecipName(c.contact_name || '');
    setRecipPhone(c.phone        || '');
    setRecipAddr(c.address       || '');
    setRecipAddrDetail('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, companies]);

  const submitAddCompany = async () => {
    const name = newCompName.trim();
    if (!name) { toast('업체명을 입력해주세요', 'err'); return; }
    if (companies.some(c => c.name === name)) {
      const exist = companies.find(c => c.name === name);
      toast(`'${name}' 업체는 이미 등록되어 있습니다`, 'err');
      if (exist) setCompanyId(String(exist.id));
      return;
    }
    setSavingCompany(true);
    const fullAddr = (newCompAddr.trim() + (newCompAddrDetail.trim() ? ' ' + newCompAddrDetail.trim() : '')).trim();
    const { data, error } = await supabase.from('biz_companies')
      .insert({
        name,
        contact_name: newCompContact.trim() || null,
        phone:        newCompPhone.trim()   || null,
        address:      fullAddr               || null,
      })
      .select()
      .single();
    setSavingCompany(false);
    if (error) { toast(error.message, 'err'); return; }
    toast(`'${name}' 추가 완료`, 'ok');
    setCompanies(prev => [...prev, data].sort((a,b) => a.name.localeCompare(b.name)));
    setCompanyId(String(data.id));
    setShowAddCompany(false);
  };

  const handleSubmit = async () => {
    if (!companyId) { toast('업체를 선택해주세요', 'err'); return; }
    if (!deliveryMethod) { toast('배송방법을 선택해주세요', 'err'); return; }
    const validLines = lines.filter(l =>
      l.productId && Number(l.quantity) > 0   // 공급가 0 허용 (상품+수량만 있으면 등록)
    );
    if (validLines.length === 0) { toast('상품을 1개 이상 입력해주세요', 'err'); return; }
    const company = companies.find(c => String(c.id) === String(companyId));
    setSaving(true);
    try {
      const fullAddr = (recipAddr.trim() + (recipAddrDetail.trim() ? ' ' + recipAddrDetail.trim() : '')).trim();
      for (const l of validLines) {
        const prod  = allProds.find(p => String(p.id) === String(l.productId));
        const brand = brands.find(b => String(b.id) === String(l.brandId));
        const { error } = await supabase.from('biz_sales').insert({
          sold_at: soldAt,
          company_id: Number(companyId), company_name: company?.name,
          brand_id: Number(l.brandId)||null, brand_name: brand?.name,
          product_id: Number(l.productId), product_name: prod?.name,
          quantity: Number(l.quantity)||1,
          supply_price: Number(l.supplyPrice)||0,
          memo: memo.trim()||null,
          delivery_method: deliveryMethod,
          recipient_name:    recipName.trim()  || null,
          recipient_phone:   recipPhone.trim() || null,
          recipient_address: fullAddr           || null,
          created_by: profile.id,
        });
        if (error) throw error;
      }
      toast(`특판 매출 ${validLines.length}건 등록 완료`, 'ok');
      setCompanyId(''); setDeliveryMethod(''); setMemo(''); setSoldAt(today);
      setRecipName(''); setRecipPhone(''); setRecipAddr(''); setRecipAddrDetail('');
      setLines([newLine()]);
      if (tab === 'list') fetchSales();
    } catch (err) {
      toast('저장 실패: ' + (err.message || err), 'err');
    }
    setSaving(false);
  };

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditData({
      sold_at:         s.sold_at || '',
      quantity:        s.quantity || 0,
      supply_price:    s.supply_price || 0,
      memo:            s.memo || '',
      delivery_method: s.delivery_method || '',
    });
  };
  const cancelEdit = () => { setEditingId(null); setEditData({}); };
  const saveEdit = async (id) => {
    if (!editData.sold_at) { toast('매출일을 입력해주세요', 'err'); return; }
    setSavingEdit(true);
    const { error } = await supabase.from('biz_sales').update({
      sold_at:         editData.sold_at,
      quantity:        Number(editData.quantity) || 0,
      supply_price:    Number(editData.supply_price) || 0,
      memo:            (editData.memo || '').trim() || null,
      delivery_method: editData.delivery_method || null,
    }).eq('id', id);
    setSavingEdit(false);
    if (error) { toast(error.message, 'err'); return; }
    toast('수정 완료', 'ok');
    cancelEdit();
    fetchSales();
  };
  const deleteRow = async (s) => {
    const name = s.product?.name || s.product_name || '상품';
    if (!window.confirm(`'${name}' 특판 매출 라인을 삭제하시겠습니까?\n(되돌릴 수 없습니다)`)) return;
    const { error } = await supabase.from('biz_sales').delete().eq('id', s.id);
    if (error) { toast(error.message, 'err'); return; }
    toast('삭제 완료', 'inf');
    if (editingId === s.id) cancelEdit();
    fetchSales();
  };

  // 반품 — 원매출 기준 음수수량 행을 추가해 매출에서 차감
  const openReturn = (s) => {
    setReturnTarget(s);
    setReturnDate(today);
    setReturnQty(Math.max(1, Number(s.quantity) || 1));
    setReturnMemo('');
  };
  const closeReturn = () => { setReturnTarget(null); setReturnMemo(''); };
  const submitReturn = async () => {
    const s = returnTarget;
    if (!s) return;
    const qty = Number(returnQty) || 0;
    const maxQty = Number(s.quantity) || 0;
    if (!returnDate) { toast('반품날짜를 입력해주세요', 'err'); return; }
    if (qty <= 0) { toast('반품수량은 1 이상이어야 합니다', 'err'); return; }
    if (qty > maxQty) { toast(`반품수량은 원매출 수량(${maxQty})을 초과할 수 없습니다`, 'err'); return; }
    setSavingReturn(true);
    const { error } = await supabase.from('biz_sales').insert({
      sold_at: returnDate,
      company_id:   s.company_id || null,
      company_name: s.company?.name || s.company_name || null,
      brand_id:     s.brand_id || null,
      brand_name:   s.brand?.name || s.brand_name || null,
      product_id:   s.product_id || null,
      product_name: s.product?.name || s.product_name || null,
      quantity:     -Math.abs(qty),
      supply_price: Number(s.supply_price) || 0,
      is_return:    true,
      return_of:    s.id,
      memo:         returnMemo.trim() || `반품 (원매출 #${s.id})`,
      delivery_method: null,
      created_by:   profile.id,
    });
    setSavingReturn(false);
    if (error) { toast(error.message, 'err'); return; }
    const amt = (Number(s.supply_price) || 0) * qty;
    toast(`반품 처리 완료 (${qty}개 · -${amt.toLocaleString()}원)`, 'ok');
    closeReturn();
    fetchSales();
  };
  const cancelReturn = async (s) => {
    if (!window.confirm(`이 반품 내역을 취소(삭제)하시겠습니까?\n매출 차감이 되돌려집니다.`)) return;
    const { error } = await supabase.from('biz_sales').delete().eq('id', s.id);
    if (error) { toast(error.message, 'err'); return; }
    toast('반품 취소 완료', 'inf');
    fetchSales();
  };

  // 입력 합계
  const totalAmt = lines.reduce((s,l) => s + (Number(l.quantity)||0) * (Number(l.supplyPrice)||0), 0);
  const totalQty = lines.reduce((s,l) => s + (Number(l.quantity)||0), 0);

  // 조회 합계
  const listTotalAmt = sales.reduce((s,r) => s + Math.round((r.supply_price||0) * (r.quantity||0)), 0);
  const listTotalQty = sales.reduce((s,r) => s + (r.quantity||0), 0);

  // 특판매출 엑셀 (매출raw 양식). 그룹=특판, 전표유형=정상 통일.
  const exportExcel = async () => {
    if (sales.length === 0) { toast('조회된 데이터가 없습니다', 'err'); return; }
    const ExcelJS = (await import('exceljs')).default;
    const { dlBlob } = await import('../../lib/utils');
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('매출raw');
    [4, 10.8, 11.3, 14.8, 20.1, 12.4, 17, 52.9, 7.8, 12.8, 16.4, 16, 14.4]
      .forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    ws.addRow(['선택','순번','매출일자','그룹','매장','전표\n유형','상품코드','상품명','매출\n수량','최종금액','원가','최종원가','']);
    sales.forEach((r, idx) => {
      const code = r.product?.code || r.product_code || '';
      const cost = Number(r.product?.cost ?? r.unit_cost ?? 0);
      const name = r.product?.name || r.product_name || '';
      const company = r.company?.name || r.company_name || '';
      const qty = Number(r.quantity) || 0;
      const unit = Number(r.supply_price) || 0;
      const amount = Math.round(unit * qty);
      const costTotal = Math.round(cost * qty);
      const ratio = amount > 0 ? costTotal / amount : '';
      ws.addRow(['0', idx + 1, new Date((r.sold_at || '') + 'T12:00:00'), '특판', company, '정상',
        (code !== '' && !isNaN(Number(code))) ? Number(code) : code, name, qty, amount, Math.round(cost), costTotal, ratio]);
    });
    const font = { name: '굴림', size: 9 };
    const lastRow = ws.rowCount;
    ws.getRow(1).eachCell(c => { c.font = { ...font, bold: true }; c.alignment = { horizontal:'center', vertical:'center', wrapText:true }; });
    ws.getRow(1).height = 28;
    for (let rn = 2; rn <= lastRow; rn++) {
      const row = ws.getRow(rn);
      row.eachCell(c => { c.font = font; });
      row.getCell(3).numFmt = '[$-412]yyyy-mm-dd';
      [10, 11, 12].forEach(c => row.getCell(c).numFmt = '#,##0');
    }
    const buf = await wb.xlsx.writeBuffer();
    const scope = fCompany
      ? (String(fCompany).startsWith('sp:') ? fCompany.slice(3) : (companies.find(c=>String(c.id)===String(fCompany))?.name || '업체'))
      : '전체';
    dlBlob(buf, `특판매출_${scope}_${fFrom}~${fTo}.xlsx`);
    toast(`${sales.length}건 다운로드`, 'ok');
  };

  const inputStyle = {height:36, padding:'0 10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:13, fontFamily:'var(--sans)', outline:'none', width:'100%'};
  const labelStyle = {display:'block', fontSize:11, fontWeight:600, color:'var(--text2)', marginBottom:4};
  const GRID_COLS = 'minmax(220px, 1fr) 70px 180px 110px 80px 36px';

  return (
    <div>
      {setPage && <SalesTabNav current="biz_sales_view" setPage={setPage}/>}
      {/* 탭 — full 모드에서만 노출 (view/input은 단일 화면) */}
      {mode === 'full' && (
        <div style={{display:'flex', gap:8, marginBottom:16}}>
          {[{key:'list',label:'📋 특판 매출 조회'},{key:'input',label:'➕ 매출 입력'}].map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)}
              style={{height:36, padding:'0 18px', border:'2px solid', borderRadius:'var(--radius)', fontSize:12, fontWeight:700, cursor:'pointer',
                borderColor: tab===t.key?'var(--accent)':'var(--border)',
                background:  tab===t.key?'#fff3e0':'#fff',
                color:       tab===t.key?'var(--accent)':'var(--text2)'}}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* 매출 입력 탭 */}
      {tab==='input' && (
        <div className="card">
          <div className="card-label">특판 매출 입력</div>

          {/* 거래 단위 정보 */}
          <div style={{display:'grid', gridTemplateColumns:'150px 1fr 230px', gap:12, marginBottom:14}}>
            <div>
              <label style={labelStyle}>매출일 <span style={{color:'var(--danger)'}}>*</span></label>
              <input type="date" value={soldAt} onChange={e=>setSoldAt(e.target.value)} style={inputStyle}/>
            </div>
            <div>
              <label style={labelStyle}>업체 <span style={{color:'var(--danger)'}}>*</span></label>
              <div style={{display:'flex', gap:6}}>
                <select value={companyId} onChange={e=>setCompanyId(e.target.value)} style={{...inputStyle, flex:1}}>
                  <option value="">-- 업체 선택 --</option>
                  {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button type="button" onClick={openAddCompanyModal}
                  title="신규업체 추가"
                  style={{height:36, padding:'0 12px', border:'1px solid var(--accent)', background:'#fff3e0', color:'var(--accent)', borderRadius:'var(--radius)', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap'}}>
                  + 신규
                </button>
              </div>
            </div>
            <div>
              <label style={labelStyle}>배송방법 <span style={{color:'var(--danger)'}}>*</span></label>
              <div style={{display:'flex', gap:6}}>
                {[{key:'용차', label:'🚚 용차'}, {key:'택배', label:'📦 택배'}].map(m => {
                  const active = deliveryMethod === m.key;
                  return (
                    <button key={m.key} type="button" onClick={() => setDeliveryMethod(m.key)}
                      style={{flex:1, height:36, border:'1px solid', borderRadius:'var(--radius)', fontSize:13, fontWeight:700, cursor:'pointer',
                        borderColor: active ? 'var(--accent)' : 'var(--border)',
                        background:  active ? '#fff3e0' : '#fff',
                        color:       active ? 'var(--accent)' : 'var(--text2)'}}>
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 상품 라인 */}
          <div className="card-label" style={{marginTop:4}}>🛍️ 상품 목록</div>

          {/* 헤더 라벨 */}
          <div style={{ display:'grid', gridTemplateColumns:GRID_COLS, gap:6, padding:'0 4px 6px', fontSize:11, fontWeight:700, color:'var(--text3)' }}>
            <div>상품검색</div>
            <div style={{textAlign:'center'}}>수량</div>
            <div style={{textAlign:'center'}}>공급가</div>
            <div style={{textAlign:'right'}}>합계</div>
            <div></div>
            <div></div>
          </div>

          {lines.map((l, idx) => {
            const suggestions = l.productSearch && l.productSearch.length >= 1
              ? allProds
                  .filter(p => {
                    const q = l.productSearch.toLowerCase();
                    return (p.name || '').toLowerCase().includes(q)
                        || (p.code || '').toLowerCase().includes(q)
                        || (p.erp_code || '').toLowerCase().includes(q);
                  })
                  .sort((a,b) => (a.name.includes('[단종]')?1:0) - (b.name.includes('[단종]')?1:0))
                  .slice(0, 10)
              : [];
            const selProd = allProds.find(p => String(p.id) === String(l.productId));
            const lineSubtotal = (Number(l.quantity)||0) * (Number(l.supplyPrice)||0);
            const isLast = idx === lines.length - 1;

            return (
              <div key={l.id} style={{ background: idx%2===0?'#fafafa':'#f0f7ff', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px 8px', marginBottom:6 }}>
                <div style={{ display:'grid', gridTemplateColumns:GRID_COLS, gap:6, alignItems:'center' }}>
                  {/* 상품검색 */}
                  <div style={{ position:'relative' }}>
                    <input
                      value={l.productSearch !== undefined ? l.productSearch : (selProd?.name || '')}
                      onChange={e => {
                        updateLine(l.id,'productSearch',e.target.value);
                        updateLine(l.id,'productId','');
                        updateLine(l.id,'showSuggestions',true);
                      }}
                      onFocus={() => updateLine(l.id,'showSuggestions',true)}
                      onBlur={() => setTimeout(() => updateLine(l.id,'showSuggestions',false), 200)}
                      style={{...inputStyle, height:38, background:'#fff'}}
                      placeholder="상품명 / 상품코드 / ERP 검색"
                      autoComplete="off"
                    />
                    {l.showSuggestions && suggestions.length > 0 && (
                      <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:100, background:'#fff', border:'1px solid var(--border)', borderRadius:'var(--radius)', boxShadow:'0 4px 16px rgba(0,0,0,0.12)', maxHeight:220, overflowY:'auto', marginTop:2 }}>
                        {suggestions.map(p => {
                          const brand = brands.find(b => b.id === p.brand_id);
                          return (
                            <div key={p.id}
                              onMouseDown={e => { e.preventDefault(); selectProdForLine(l.id, p); }}
                              style={{ padding:'8px 12px', cursor:'pointer', fontSize:13, borderBottom:'1px solid #f0f0f0' }}
                              onMouseEnter={e => e.currentTarget.style.background='#fffde7'}
                              onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                              <div style={{display:'flex', alignItems:'center'}}>
                                {brand && <span style={{fontSize:11, color:'var(--accent)', fontWeight:700, marginRight:6}}>[{brand.name}]</span>}
                                <span>{p.name}</span>
                                <span style={{ fontSize:11, color:'var(--text3)', marginLeft:'auto', fontFamily:'var(--mono)' }}>{Number(p.price).toLocaleString()}원</span>
                              </div>
                              {(p.code || p.erp_code) && (
                                <div style={{display:'flex', gap:10, marginTop:3, fontSize:10, fontFamily:'var(--mono)', color:'var(--text3)'}}>
                                  {p.code && <span>상품코드: <strong style={{color:'var(--text2)'}}>{p.code}</strong></span>}
                                  {p.erp_code && <span>ERP: <strong style={{color:'var(--text2)'}}>{p.erp_code}</strong></span>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {/* 수량 */}
                  <input type="number" min={1} value={l.quantity}
                    onChange={e => updateLine(l.id,'quantity',e.target.value)}
                    style={{...inputStyle, height:38, textAlign:'center'}} required />
                  {/* 공급가 + 증정 토글 */}
                  <div style={{ display:'flex', gap:4 }}>
                    <input type="text" inputMode="numeric" value={l.isGift ? '0' : formatNumInput(l.supplyPrice)}
                      onChange={e => updateLine(l.id,'supplyPrice',parseNumInput(e.target.value))}
                      disabled={l.isGift}
                      style={{...inputStyle, height:38, flex:1, textAlign:'right', fontWeight:700,
                        color: l.isGift ? 'var(--text3)' : 'var(--accent)',
                        background: l.isGift ? '#f5f5f5' : '#fff'}}
                      placeholder="0" />
                    <button type="button"
                      onClick={() => {
                        const turningOn = !l.isGift;
                        updateLine(l.id, 'isGift', turningOn);
                        if (turningOn) updateLine(l.id, 'supplyPrice', '0');
                      }}
                      title={l.isGift ? '증정 해제' : '증정으로 처리 (공급가 0원)'}
                      style={{ height:38, padding:'0 8px', border:'1px solid', borderRadius:'var(--radius)', fontSize:11, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap',
                        borderColor: l.isGift ? '#6a1b9a' : 'var(--border)',
                        background:  l.isGift ? '#f3e5f5' : '#fff',
                        color:       l.isGift ? '#6a1b9a' : 'var(--text2)'}}>
                      🎁 증정
                    </button>
                  </div>
                  {/* 합계 */}
                  <div style={{textAlign:'right', fontFamily:'var(--mono)', fontSize:13, fontWeight:700, color:'var(--accent)'}}>
                    {lineSubtotal > 0 ? lineSubtotal.toLocaleString()+'원' : '-'}
                  </div>
                  {/* 상품추가 (마지막 라인에만) */}
                  {isLast ? (
                    <button type="button" onClick={addLine}
                      title="상품 추가"
                      style={{ height:38, width:'100%', border:'1px solid var(--accent)', background:'#fff3e0', color:'var(--accent)', borderRadius:'var(--radius)', cursor:'pointer', fontSize:12, fontWeight:700, lineHeight:1, padding:0, whiteSpace:'nowrap' }}>상품추가</button>
                  ) : <div/>}
                  {/* ✕ 삭제 / 초기화 */}
                  <button type="button" onClick={() => removeLine(l.id)}
                    title={lines.length > 1 ? '삭제' : '초기화'}
                    style={{ height:38, width:36, border:'1px solid var(--border)', background:'#fff', color:'var(--danger)', borderRadius:'var(--radius)', cursor:'pointer', fontSize:14, lineHeight:1, padding:0 }}>✕</button>
                </div>
                {/* 상품코드 / ERP 표시 */}
                {selProd && (selProd.code || selProd.erp_code) && (
                  <div style={{marginTop:6, fontSize:11, color:'var(--text3)', fontFamily:'var(--mono)', display:'flex', gap:12}}>
                    {selProd.code && <span>상품코드: <strong style={{color:'var(--text2)'}}>{selProd.code}</strong></span>}
                    {selProd.erp_code && <span>ERP: <strong style={{color:'var(--text2)'}}>{selProd.erp_code}</strong></span>}
                  </div>
                )}
              </div>
            );
          })}

          {/* 합계 */}
          {totalAmt > 0 && (
            <div style={{ background:'#fff3e0', border:'1px solid #ffcc80', borderRadius:'var(--radius)', padding:'10px 14px', textAlign:'right', fontSize:14, fontWeight:700, color:'var(--accent)', fontFamily:'var(--mono)', marginTop:6 }}>
              총 합계: {totalAmt.toLocaleString()}원 ({lines.filter(l => l.productId).length}개 상품 / 총 {totalQty}개)
            </div>
          )}

          {/* 메모 */}
          {/* 배송지 정보 (선택) */}
          <div style={{ background:'#fff8e1', border:'1px solid #ffcc80', borderRadius:'var(--radius)', padding:'14px 16px', marginTop:14, marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#e65100', marginBottom:10 }}>
              📦 배송지 정보 <span style={{fontSize:11, color:'var(--text3)', fontWeight:500}}>(업체 선택 시 자동 입력, 수정 가능)</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
              <div>
                <label style={labelStyle}>받는사람</label>
                <input value={recipName} onChange={e => setRecipName(e.target.value)}
                  style={inputStyle} placeholder="예: 홍길동" />
              </div>
              <div>
                <label style={labelStyle}>연락처</label>
                <input value={recipPhone} onChange={e => setRecipPhone(e.target.value)}
                  style={inputStyle} placeholder="010-1234-5678" />
              </div>
            </div>
            <div>
              <label style={labelStyle}>주소</label>
              <div style={{display:'flex', gap:6}}>
                <input value={recipAddr} onChange={e => setRecipAddr(e.target.value)}
                  style={{...inputStyle, flex:1}} placeholder="🔍 주소검색 버튼 또는 직접 입력" />
                <button type="button" onClick={openShippingAddrSearch}
                  title="다음(카카오) 우편번호 검색"
                  style={{height:36, padding:'0 14px', border:'1px solid var(--accent)', background:'#fff3e0', color:'var(--accent)', borderRadius:'var(--radius)', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap'}}>
                  🔍 주소검색
                </button>
              </div>
              <input id="biz-recip-addr-detail" value={recipAddrDetail}
                onChange={e => setRecipAddrDetail(e.target.value)}
                style={{...inputStyle, marginTop:6}}
                placeholder="상세주소 (동·호수 등, 선택)" />
            </div>
          </div>

          <div style={{marginTop:0, marginBottom:12}}>
            <label style={labelStyle}>메모</label>
            <input value={memo} onChange={e=>setMemo(e.target.value)} placeholder="메모 (선택)" style={inputStyle}/>
          </div>

          <button className="btn btn-p" onClick={handleSubmit} disabled={saving}
            style={{width:'100%', height:40, fontSize:14, justifyContent:'center'}}>
            {saving ? <span className="spinner"/> : '✅ 특판 매출 등록'}
          </button>
        </div>
      )}

      {/* 매출 조회 탭 */}
      {tab==='list' && (
        <div className="card" style={{padding:0, overflow:'hidden'}}>
          <div style={{display:'flex', alignItems:'center', gap:8, padding:'14px 20px', borderBottom:'1px solid var(--border)', flexWrap:'wrap'}}>
            {(() => { const selStyle = {height:36, padding:'0 10px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:13, fontFamily:'var(--sans)', outline:'none'}; return (
            <>
            <input type="date" value={fFrom} onChange={e=>setFFrom(e.target.value)} style={selStyle} title="매출일 시작"/>
            <span style={{fontSize:12, color:'var(--text3)'}}>~</span>
            <input type="date" value={fTo} onChange={e=>setFTo(e.target.value)} style={selStyle} title="매출일 종료"/>
            <button className="btn btn-s" onClick={()=>{ setFFrom(todayStr); setFTo(todayStr); }}>오늘</button>
            <button className="btn btn-s" onClick={()=>{ setFFrom(monthStart); setFTo(todayStr); }}>이번달</button>
            <select value={fCompany} onChange={e=>setFCompany(e.target.value)} style={selStyle}>
              <option value="">전체 업체</option>
              {companies.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              {(() => {
                // 매장 판매입력으로 들어온 특판 중, B2B 업체 목록에 없는 실제 데이터만 같은 목록에 합쳐 표시
                const compNames = new Set(companies.map(c => c.name));
                const opts = specialNames.filter(n => !compNames.has(n)).sort();
                return opts.map(n=><option key={'sp:'+n} value={'sp:'+n}>{n}</option>);
              })()}
            </select>
            {fCompany && <button className="btn-ghost" onClick={()=>setFCompany('')}>✕</button>}
            <button type="button" onClick={exportExcel}
              style={{height:36, padding:'0 12px', border:'1px solid #2e7d32', borderRadius:'var(--radius)', background:'#e8f5e9', color:'#2e7d32', fontSize:12, fontWeight:700, cursor:'pointer'}}>
              📥 엑셀
            </button>
            </>
            ); })()}
            <div style={{marginLeft:'auto', textAlign:'right'}}>
              <div style={{fontSize:11, color:'var(--text3)'}}>총 매출</div>
              <div style={{fontSize:18, fontWeight:700, color:'var(--accent)', fontFamily:'var(--mono)'}}>{listTotalAmt.toLocaleString()}원</div>
            </div>
          </div>
          {loading ? <div className="empty"><span className="spinner"/></div> : sales.length===0 ? (
            <div className="empty">데이터가 없습니다</div>
          ) : (
            <div className="twrap">
              <table>
                <thead>
                  <tr>
                    <th>날짜</th><th>업체</th><th>브랜드</th><th>상품명</th>
                    <th className="r">수량</th><th className="r">공급가</th><th className="r">합계</th>
                    <th style={{width:80}}>배송</th><th>메모</th>
                    {canManage && <th style={{width:170, textAlign:'center'}}>관리</th>}
                  </tr>
                </thead>
                <tbody>
                  {sales.map(s => {
                    const isEditing = editingId === s.id;
                    const numInput = { width:70, height:28, padding:'0 6px', border:'1px solid var(--accent)', borderRadius:'var(--radius)', fontSize:12, textAlign:'right', outline:'none', fontFamily:'var(--mono)' };
                    return (
                    <tr key={s.id} style={isEditing ? {background:'#fffde7'} : s.is_return ? {background:'#fff5f5'} : {}}>
                      <td className="mono" style={{fontSize:12}}>
                        {isEditing
                          ? <input type="date" value={editData.sold_at}
                              onChange={e => setEditData(p => ({...p, sold_at:e.target.value}))}
                              style={{width:130, height:28, padding:'0 6px', border:'1px solid var(--accent)', borderRadius:'var(--radius)', fontSize:12, outline:'none'}}/>
                          : s.sold_at}
                      </td>
                      <td style={{fontWeight:600}}>
                        {s.company?.name||s.company_name}
                        {s._special && <span className="badge badge-store" style={{marginLeft:6}}>매장</span>}
                        {s.is_return && <span className="badge" style={{marginLeft:6, background:'#ffebee', color:'var(--danger)', border:'1px solid #ef9a9a'}}>반품</span>}
                      </td>
                      <td><span className="badge badge-dept">{s.brand?.name||s.brand_name||'-'}</span></td>
                      <td style={{fontSize:13}}>{s.product?.name||s.product_name}</td>
                      <td className="r" style={{fontFamily:'var(--mono)'}}>
                        {isEditing
                          ? <input type="number" min={0} value={editData.quantity}
                              onChange={e => setEditData(p => ({...p, quantity:e.target.value}))}
                              style={{...numInput, width:60}}/>
                          : s.quantity}
                      </td>
                      <td className="r" style={{fontFamily:'var(--mono)'}}>
                        {isEditing
                          ? <input type="text" inputMode="numeric" value={formatNumInput(editData.supply_price)}
                              onChange={e => setEditData(p => ({...p, supply_price:parseNumInput(e.target.value)}))}
                              style={numInput}/>
                          : `${Math.round(s.supply_price||0).toLocaleString()}원`}
                      </td>
                      <td className="r" style={{fontFamily:'var(--mono)', fontWeight:700, color:'var(--accent)'}}>
                        {Math.round((((isEditing ? Number(editData.supply_price) : s.supply_price) || 0) *
                          ((isEditing ? Number(editData.quantity) : s.quantity) || 0))).toLocaleString()}원
                      </td>
                      <td style={{fontSize:11}}>
                        {isEditing
                          ? <select value={editData.delivery_method}
                              onChange={e => setEditData(p => ({...p, delivery_method:e.target.value}))}
                              style={{height:28, padding:'0 4px', border:'1px solid var(--accent)', borderRadius:'var(--radius)', fontSize:11}}>
                              <option value="">-</option>
                              <option value="용차">🚚 용차</option>
                              <option value="택배">📦 택배</option>
                            </select>
                          : (s.delivery_method === '용차' ? '🚚 용차' : s.delivery_method === '택배' ? '📦 택배' : '-')}
                      </td>
                      <td style={{fontSize:11, color:'var(--text2)'}}>
                        {isEditing
                          ? <input type="text" value={editData.memo}
                              onChange={e => setEditData(p => ({...p, memo:e.target.value}))}
                              style={{width:'100%', height:28, padding:'0 6px', border:'1px solid var(--accent)', borderRadius:'var(--radius)', fontSize:11, outline:'none'}}/>
                          : (s.memo || '-')}
                      </td>
                      {canManage && (
                        <td style={{textAlign:'center'}}>
                          {s._special ? (
                            <span style={{fontSize:11, color:'var(--text3)'}}>판매입력</span>
                          ) : isEditing ? (
                            <div style={{display:'flex', gap:4, justifyContent:'center'}}>
                              <button className="btn btn-p" style={{height:26, padding:'0 10px', fontSize:11}} disabled={savingEdit} onClick={() => saveEdit(s.id)}>
                                {savingEdit ? <span className="spinner"/> : '저장'}
                              </button>
                              <button className="btn btn-s" style={{height:26, padding:'0 8px', fontSize:11}} onClick={cancelEdit}>취소</button>
                            </div>
                          ) : s.is_return ? (
                            <button className="btn-danger" style={{height:26, padding:'0 10px', fontSize:11, whiteSpace:'nowrap'}} onClick={() => cancelReturn(s)}>반품취소</button>
                          ) : (
                            <div style={{display:'flex', gap:4, justifyContent:'center'}}>
                              <button className="btn btn-s" style={{height:26, padding:'0 8px', fontSize:11, whiteSpace:'nowrap', flexShrink:0}} onClick={() => startEdit(s)}>수정</button>
                              <button className="btn-danger" style={{height:26, padding:'0 8px', fontSize:11, whiteSpace:'nowrap', flexShrink:0}} onClick={() => deleteRow(s)}>삭제</button>
                              <button type="button" style={{height:26, padding:'0 8px', fontSize:11, border:'1px solid #e65100', borderRadius:'var(--radius)', background:'#fff3e0', color:'#e65100', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0}} onClick={() => openReturn(s)}>반품</button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );})}
                  <tr style={{background:'var(--bg3)', borderTop:'2px solid var(--border2)'}}>
                    <td colSpan={4} style={{padding:'10px 11px', fontWeight:700}}>합계</td>
                    <td className="r" style={{fontFamily:'var(--mono)', fontWeight:700, padding:'10px 11px'}}>{listTotalQty}</td>
                    <td/>
                    <td className="r" style={{fontFamily:'var(--mono)', fontWeight:700, fontSize:14, color:'var(--accent)', padding:'10px 11px'}}>{listTotalAmt.toLocaleString()}원</td>
                    <td/>
                    <td/>
                    {canManage && <td/>}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 신규업체 추가 모달 */}
      {showAddCompany && (
        <div style={{position:'fixed', inset:0, zIndex:300, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.45)'}} onClick={() => !savingCompany && setShowAddCompany(false)}/>
          <div style={{position:'relative', background:'#fff', borderRadius:12, padding:'22px 24px', width:480, maxWidth:'92vw', boxShadow:'0 8px 40px rgba(0,0,0,0.22)'}}>
            <div style={{display:'flex', alignItems:'center', marginBottom:16}}>
              <div style={{fontSize:16, fontWeight:700, color:'var(--text)'}}>🏢 신규업체 추가</div>
              <button type="button" onClick={() => !savingCompany && setShowAddCompany(false)}
                style={{marginLeft:'auto', background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999'}}>✕</button>
            </div>
            <div style={{marginBottom:10}}>
              <label style={labelStyle}>업체명 <span style={{color:'var(--danger)'}}>*</span></label>
              <input value={newCompName} onChange={e => setNewCompName(e.target.value)}
                style={inputStyle} placeholder="예: ○○유통" autoFocus />
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10}}>
              <div>
                <label style={labelStyle}>담당자명</label>
                <input value={newCompContact} onChange={e => setNewCompContact(e.target.value)}
                  style={inputStyle} placeholder="예: 홍길동" />
              </div>
              <div>
                <label style={labelStyle}>연락처</label>
                <input value={newCompPhone} onChange={e => setNewCompPhone(e.target.value)}
                  style={inputStyle} placeholder="010-1234-5678" />
              </div>
            </div>
            <div style={{marginBottom:14}}>
              <label style={labelStyle}>주소</label>
              <div style={{display:'flex', gap:6}}>
                <input value={newCompAddr} onChange={e => setNewCompAddr(e.target.value)}
                  style={{...inputStyle, flex:1}} placeholder="🔍 주소검색 버튼 또는 직접 입력" />
                <button type="button" onClick={openCompanyAddrSearch}
                  title="다음(카카오) 우편번호 검색"
                  style={{height:36, padding:'0 14px', border:'1px solid var(--accent)', background:'#fff3e0', color:'var(--accent)', borderRadius:'var(--radius)', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap'}}>
                  🔍 주소검색
                </button>
              </div>
              <input id="new-comp-addr-detail" value={newCompAddrDetail} onChange={e => setNewCompAddrDetail(e.target.value)}
                style={{...inputStyle, marginTop:6}} placeholder="상세주소 (동·호수 등, 선택)" />
            </div>
            <div style={{display:'flex', gap:8}}>
              <button type="button" onClick={() => setShowAddCompany(false)} disabled={savingCompany}
                style={{flex:1, height:42, border:'1px solid var(--border)', background:'#fff', color:'var(--text2)', borderRadius:'var(--radius)', fontSize:13, fontWeight:600, cursor:'pointer'}}>
                취소
              </button>
              <button type="button" onClick={submitAddCompany} disabled={savingCompany}
                style={{flex:2, height:42, border:'none', background:'var(--accent)', color:'#fff', borderRadius:'var(--radius)', fontSize:14, fontWeight:700, cursor:'pointer'}}>
                {savingCompany ? <span className="spinner"/> : '✅ 업체 등록'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 반품 처리 모달 */}
      {returnTarget && (
        <div style={{position:'fixed', inset:0, zIndex:300, display:'flex', alignItems:'center', justifyContent:'center'}}>
          <div style={{position:'absolute', inset:0, background:'rgba(0,0,0,0.45)'}} onClick={() => !savingReturn && closeReturn()}/>
          <div style={{position:'relative', background:'#fff', borderRadius:12, padding:'22px 24px', width:440, maxWidth:'92vw', boxShadow:'0 8px 40px rgba(0,0,0,0.22)'}}>
            <div style={{display:'flex', alignItems:'center', marginBottom:14}}>
              <div style={{fontSize:16, fontWeight:700, color:'var(--danger)'}}>↩️ 특판 매출 반품</div>
              <button type="button" onClick={() => !savingReturn && closeReturn()}
                style={{marginLeft:'auto', background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#999'}}>✕</button>
            </div>
            <div style={{background:'#fafafa', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'10px 12px', marginBottom:14, fontSize:13}}>
              <div style={{fontWeight:700, marginBottom:4}}>{returnTarget.company?.name || returnTarget.company_name}</div>
              <div style={{color:'var(--text2)'}}>{returnTarget.product?.name || returnTarget.product_name}</div>
              <div style={{color:'var(--text3)', fontSize:12, marginTop:4, fontFamily:'var(--mono)'}}>
                원매출: {returnTarget.quantity}개 · 단가 {Math.round(returnTarget.supply_price||0).toLocaleString()}원 ({returnTarget.sold_at})
              </div>
            </div>
            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12}}>
              <div>
                <label style={labelStyle}>반품날짜 <span style={{color:'var(--danger)'}}>*</span></label>
                <input type="date" value={returnDate} onChange={e => setReturnDate(e.target.value)} style={inputStyle}/>
              </div>
              <div>
                <label style={labelStyle}>반품수량 <span style={{color:'var(--danger)'}}>*</span> <span style={{color:'var(--text3)', fontWeight:500}}>(최대 {returnTarget.quantity})</span></label>
                <input type="number" min={1} max={returnTarget.quantity} value={returnQty}
                  onChange={e => setReturnQty(e.target.value)} style={{...inputStyle, textAlign:'right'}}/>
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <label style={labelStyle}>메모 (선택)</label>
              <input value={returnMemo} onChange={e => setReturnMemo(e.target.value)} placeholder="반품 사유 등" style={inputStyle}/>
            </div>
            <div style={{background:'#fff3e0', border:'1px solid #ffcc80', borderRadius:'var(--radius)', padding:'8px 12px', marginBottom:14, fontSize:13, textAlign:'right', fontFamily:'var(--mono)', fontWeight:700, color:'var(--danger)'}}>
              차감 금액: -{((Number(returnTarget.supply_price)||0) * (Number(returnQty)||0)).toLocaleString()}원
            </div>
            <div style={{display:'flex', gap:8}}>
              <button type="button" onClick={closeReturn} disabled={savingReturn}
                style={{flex:1, height:42, border:'1px solid var(--border)', background:'#fff', color:'var(--text2)', borderRadius:'var(--radius)', fontSize:13, fontWeight:600, cursor:'pointer'}}>
                취소
              </button>
              <button type="button" onClick={submitReturn} disabled={savingReturn}
                style={{flex:2, height:42, border:'none', background:'var(--danger)', color:'#fff', borderRadius:'var(--radius)', fontSize:14, fontWeight:700, cursor:'pointer'}}>
                {savingReturn ? <span className="spinner"/> : '✅ 반품 처리'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
